import React, { useEffect } from "react";
import { useTts } from "./tts-context";
import { useSignal } from "../../use-signal";
import { autoFollowSignal, currentUrlSignal, lastAutoFollowUrlSignal } from "./tts-store";
import { BookOpen, Pause, Play, SkipBack, SkipForward, Square } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { endBatch, startBatch } from "alien-signals";
import {
  bottomBarContainerStyles,
  bottomBarInnerStyles,
  bottomBarItemStyles,
} from "#/capacitor-app.css";

export function TtsPlayer() {
  const {
    isPlaying,
    serviceStopped,
    currentBookId,
    currentChapterId,
    pause,
    resume,
    stop,
    nextSentence,
    prevSentence,
    isTtsSupported,
  } = useTts();
  const navigate = useNavigate();
  const location = useLocation();

  const autoFollow = useSignal(autoFollowSignal);
  const currentUrl = useSignal(currentUrlSignal);

  // Sync currentUrlSignal with location.pathname (external navigation tracking)
  useEffect(() => {
    currentUrlSignal(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    if (!autoFollow || !currentBookId || !currentChapterId) {
      return;
    }
    const expectedUrl = `/book/${currentBookId}/chapter/${currentChapterId}`;
    // Navigate with batch update to keep signals in sync
    if (currentUrl !== expectedUrl) {
      startBatch();
      lastAutoFollowUrlSignal(expectedUrl);
      currentUrlSignal(expectedUrl);
      endBatch();
      void navigate(expectedUrl, { replace: true });
    }
  }, [currentUrl, autoFollow, currentBookId, currentChapterId, navigate]);

  const handleToggleAutoFollow = () => {
    const newState = !autoFollow;
    startBatch();
    if (newState && currentBookId && currentChapterId) {
      const targetUrl = `/book/${currentBookId}/chapter/${currentChapterId}`;
      lastAutoFollowUrlSignal(targetUrl);
      currentUrlSignal(targetUrl);
      void navigate(targetUrl);
    }
    autoFollowSignal(newState);
    endBatch();
  };

  const shouldShow = isTtsSupported && !serviceStopped;
  if (!shouldShow) {
    return null;
  }

  return (
    <div className={bottomBarContainerStyles}>
      <div className={bottomBarInnerStyles}>
        <button className={bottomBarItemStyles()} onClick={prevSentence}>
          <SkipBack size={24} />
        </button>

        <button
          className={bottomBarItemStyles()}
          style={{ color: "var(--colors-error)" }}
          onClick={stop}
        >
          <Square size={24} />
        </button>

        {isPlaying ? (
          <button className={bottomBarItemStyles({ active: true })} onClick={pause}>
            <Pause size={24} />
          </button>
        ) : (
          <button className={bottomBarItemStyles({ active: true })} onClick={resume}>
            <Play size={24} />
          </button>
        )}

        <button
          className={bottomBarItemStyles({ active: autoFollow })}
          onClick={handleToggleAutoFollow}
          title={autoFollow ? "Auto-follow: ON" : "Auto-follow: OFF"}
        >
          <BookOpen size={24} />
        </button>

        <button className={bottomBarItemStyles()} onClick={nextSentence}>
          <SkipForward size={24} />
        </button>
      </div>
    </div>
  );
}
